/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Servicio para registro y gestión de dispositivos
 */
import api from "../axiosConfig";

// Generar un ID único para el dispositivo
const generateDeviceId = () => {
  // Intentar obtener ID guardado previamente
  let deviceId = localStorage.getItem("device_id");

  if (!deviceId) {
    // Generar nuevo UUID
    deviceId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

    // Guardar para uso futuro
    localStorage.setItem("device_id", deviceId);
  }

  return deviceId;
};

// Obtener información del dispositivo
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let deviceName = "Web Browser";
  let platform = "web";

  // Detectar tipo de dispositivo
  if (/Android/i.test(userAgent)) {
    platform = "android-web";
    deviceName = "Android Browser";
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    platform = "ios-web";
    deviceName = "iOS Browser";
  } else if (/Windows/i.test(userAgent)) {
    platform = "windows-web";
    deviceName = "Windows Browser";
  } else if (/Mac/i.test(userAgent)) {
    platform = "mac-web";
    deviceName = "Mac Browser";
  } else if (/Linux/i.test(userAgent)) {
    platform = "linux-web";
    deviceName = "Linux Browser";
  }

  // Detectar navegador
  if (/Chrome/i.test(userAgent)) {
    deviceName += " (Chrome)";
  } else if (/Firefox/i.test(userAgent)) {
    deviceName += " (Firefox)";
  } else if (/Safari/i.test(userAgent)) {
    deviceName += " (Safari)";
  } else if (/Edge/i.test(userAgent)) {
    deviceName += " (Edge)";
  }

  return {
    deviceId: generateDeviceId(),
    deviceName,
    platform,
    userAgent,
  };
};

/**
 * Registrar dispositivo después del login
 * @param {Object} options - Opciones de registro
 * @param {number} options.latitude - Latitud actual (opcional)
 * @param {number} options.longitude - Longitud actual (opcional)
 * @returns {Promise<Object>}
 */
export const registerDevice = async (options = {}) => {
  try {
    const deviceInfo = getDeviceInfo();
    const appVersion = process.env.REACT_APP_VERSION || "1.0.3";

    const payload = {
      device_id: deviceInfo.deviceId,
      device_name: deviceInfo.deviceName,
      platform: deviceInfo.platform,
      app_version: appVersion,
      user_agent: deviceInfo.userAgent,
    };

    // Agregar ubicación si está disponible
    if (options.latitude && options.longitude) {
      payload.latitude = options.latitude;
      payload.longitude = options.longitude;
    }

    const response = await api.post("/conductor/device/register", payload);

    console.log("✅ Dispositivo registrado:", response.data);

    // Guardar token de dispositivo si el servidor lo devuelve
    if (response.data?.device_token) {
      localStorage.setItem("device_token", response.data.device_token);
    }

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Error registrando dispositivo:", error);
    // No fallar silenciosamente, pero tampoco bloquear el flujo
    return {
      success: false,
      error: error.response?.data?.error || "Error al registrar dispositivo",
    };
  }
};

/**
 * Cerrar sesión en dispositivo específico
 * @returns {Promise<Object>}
 */
export const logoutDevice = async () => {
  try {
    const deviceId = localStorage.getItem("device_id");

    if (deviceId) {
      await api.post("/conductor/device/logout", { device_id: deviceId });
      console.log("✅ Sesión cerrada en dispositivo");
    }

    // Limpiar token de dispositivo
    localStorage.removeItem("device_token");

    return { success: true };
  } catch (error) {
    console.error("Error cerrando sesión en dispositivo:", error);
    return {
      success: false,
      error: error.response?.data?.error || "Error al cerrar sesión",
    };
  }
};

/**
 * Obtener lista de dispositivos del conductor
 * @returns {Promise<Array>}
 */
export const getDevices = async () => {
  try {
    const response = await api.get("/conductor/devices");
    return {
      success: true,
      devices: response.data?.devices || response.data || [],
    };
  } catch (error) {
    console.error("Error obteniendo dispositivos:", error);
    return {
      success: false,
      devices: [],
      error: error.response?.data?.error || "Error al obtener dispositivos",
    };
  }
};

/**
 * Actualizar ubicación del dispositivo
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<Object>}
 */
export const updateDeviceLocation = async (latitude, longitude) => {
  try {
    const deviceId = localStorage.getItem("device_id");

    if (!deviceId) {
      return { success: false, error: "Device ID no encontrado" };
    }

    await api.put("/conductor/device/location", {
      device_id: deviceId,
      latitude,
      longitude,
    });

    return { success: true };
  } catch (error) {
    console.error("Error actualizando ubicación:", error);
    return {
      success: false,
      error: error.response?.data?.error || "Error al actualizar ubicación",
    };
  }
};

/**
 * Obtener el ID del dispositivo actual
 * @returns {string}
 */
export const getDeviceId = () => {
  return generateDeviceId();
};

const deviceService = {
  registerDevice,
  logoutDevice,
  getDevices,
  updateDeviceLocation,
  getDeviceId,
};

export default deviceService;
